import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Calendar, Clock, ArrowLeft, Share2 } from 'lucide-react';
import { getBlogPost } from '../data/blogData';
import { useToast } from '../hooks/use-toast';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ClaudeCodeBlogContent from '../components/blog/ClaudeCodeBlogContent';

const blogComponents = {
  'ClaudeCodeBlogContent': ClaudeCodeBlogContent
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const post = getBlogPost(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-white dark:bg-black text-black dark:text-white py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-light mb-6">Post Not Found</h1>
          <Button onClick={() => navigate('/blog')} variant="outline" className="gap-2">
            <ArrowLeft size={16} />
            Back to Blog
          </Button>
        </div>
      </div>
    );
  }

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast({
      title: "Link copied!",
      description: "Blog post link copied to clipboard.",
    });
  };

  // Component-based blog posts (custom HTML layouts)
  if (post.contentType === 'component' && blogComponents[post.contentComponent]) {
    const BlogContent = blogComponents[post.contentComponent];
    return (
      <div className="min-h-screen bg-white dark:bg-black text-black dark:text-white">
        <div className="max-w-4xl mx-auto pt-20 px-6">
          <Button
            onClick={() => navigate('/blog')}
            variant="ghost"
            className="gap-2 mb-8 -ml-4"
          >
            <ArrowLeft size={16} />
            Back to Blog
          </Button>
        </div>

        <BlogContent />

        <div className="max-w-4xl mx-auto px-6 pb-20">
          <Separator className="my-12" />
          <footer className="flex items-center justify-between">
            <Button onClick={() => navigate('/blog')} variant="outline" className="gap-2">
              <ArrowLeft size={16} />
              Back to Blog
            </Button>
            <Button onClick={handleShare} variant="outline" className="gap-2">
              <Share2 size={16} />
              Share Article
            </Button>
          </footer>
        </div>
      </div>
    );
  }

  // Markdown-based blog posts (default)
  return (
    <div className="min-h-screen bg-white dark:bg-black text-black dark:text-white py-20 px-6">
      <article className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Button
          onClick={() => navigate('/blog')}
          variant="ghost"
          className="gap-2 mb-8 -ml-4"
        >
          <ArrowLeft size={16} />
          Back to Blog
        </Button>

        {/* Header */}
        <header className="mb-12">
          <div className="flex flex-wrap items-center gap-3 mb-6 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <Calendar size={16} />
              <span>{post.date}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock size={16} />
              <span>{post.readTime}</span>
            </div>
            <Badge variant="outline">{post.category}</Badge>
          </div>

          <h1 className="text-4xl md:text-5xl font-light mb-6 leading-tight">
            {post.title}
          </h1>

          <p className="text-xl text-gray-600 dark:text-gray-400 leading-relaxed">
            {post.excerpt}
          </p>

          <div className="flex items-center gap-4 mt-6">
            <Button onClick={handleShare} variant="outline" size="sm" className="gap-2">
              <Share2 size={16} />
              Share
            </Button>
          </div>
        </header>

        <Separator className="mb-12" />

        {/* Featured Image */}
        <div className="relative h-96 mb-12 overflow-hidden rounded-lg">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <ReactMarkdown
            components={{
              code({ node, inline, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || '');
                return !inline && match ? (
                  <SyntaxHighlighter
                    style={vscDarkPlus}
                    language={match[1]}
                    PreTag="div"
                    {...props}
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              },
              h2: ({ children }) => (
                <h2 className="text-3xl font-light mt-12 mb-6">{children}</h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-2xl font-light mt-8 mb-4">{children}</h3>
              ),
              p: ({ children }) => (
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">{children}</p>
              ),
              ul: ({ children }) => (
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700 dark:text-gray-300">{children}</ul>
              ),
              ol: ({ children }) => (
                <ol className="list-decimal list-inside space-y-2 mb-6 text-gray-700 dark:text-gray-300">{children}</ol>
              ),
              strong: ({ children }) => (
                <strong className="font-medium text-black dark:text-white">{children}</strong>
              ),
              a: ({ href, children }) => (
                <a href={href} className="text-blue-600 dark:text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              ),
            }}
          >
            {post.content}
          </ReactMarkdown>
        </div>

        <Separator className="my-12" />

        {/* Footer */}
        <footer className="flex items-center justify-between">
          <Button onClick={() => navigate('/blog')} variant="outline" className="gap-2">
            <ArrowLeft size={16} />
            Back to Blog
          </Button>
          <Button onClick={handleShare} variant="outline" className="gap-2">
            <Share2 size={16} />
            Share Article
          </Button>
        </footer>
      </article>
    </div>
  );
};

export default BlogPostPage;
